import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Download, FileText, Search, Loader2, ArrowRight } from 'lucide-react';
import CustomerNavigation from '@/components/CustomerNavigation';
import SEO from '@/components/SEO';

export default function CustomerReports() {
  const { user } = useAuth();
  const isDemo = isDemoUser(user);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);

  const fetchReports = useCallback(async () => {
    if (isDemo) {
      setReports([
        {
          id: 'report-1',
          title: 'Planktonanalyse - P02 Rogaland',
          date: '2025-01-15',
          area: 'P02 Rogaland',
          summary: 'Analysen viser stabile forhold pa tvers av prøvestasjonane. Totalt 12 aktive stasjonar overvåkast...',
          sample_count: 45,
          status: 'normal'
        },
        {
          id: 'report-2', 
          title: 'Ukentlig overvaaking - P02 Rogaland',
          date: '2025-01-08',
          area: 'P02 Rogaland', 
          summary: 'Moderate konsentrasjoner av kiselalger observert ved Toftoy og Foresvik...',
          sample_count: 38,
          status: 'monitor'
        }
      ]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setLoading(false);
    }
  }, [isDemo]);

  const filterReports = useCallback(() => {
    if (!searchTerm) {
      setFilteredReports(reports);
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = reports.filter(report => 
      report.title.toLowerCase().includes(term) ||
      report.area.toLowerCase().includes(term) ||
      report.date.includes(term)
    );
    setFilteredReports(filtered);
  }, [searchTerm, reports]);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { filterReports(); }, [filterReports]);

  const downloadReport = (reportId) => {
    toast.success('PDF rapport lastet ned');
  };

  const statusConfig = {
    normal: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Grøn', dot: 'bg-emerald-500' },
    monitor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Overvaaking', dot: 'bg-amber-500' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Raud', dot: 'bg-red-500' },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex">
        <CustomerNavigation />
        <main className="flex-1 min-h-screen">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 text-sm">Laster rapporter...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      <SEO title="Rapporter" description="Laboratorierapporter fra Microsafe" />
      <CustomerNavigation />
      <main className="flex-1 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8 pt-16 lg:pt-8 animate-fade-in">
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight mb-2" data-testid="reports-title">
              Laboratorierapporter
            </h1>
            <p className="text-slate-500">Historiske rapporter fra Microsafe</p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                data-testid="reports-search-input"
                type="text"
                placeholder="Sok i rapporter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 h-11 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all"
              />
            </div>
          </div>

          {/* Reports */}
          {filteredReports.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Ingen rapporter funnet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReports.map((report, i) => {
                const sc = statusConfig[report.status] || statusConfig.normal;
                return (
                  <div 
                    key={report.id} 
                    data-testid={`report-card-${report.id}`}
                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-sky-200 hover:shadow-sm transition-all group"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-heading font-semibold text-slate-900 truncate">{report.title}</h3>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {sc.label}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-5 mb-3 text-sm text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{new Date(report.date).toLocaleDateString('nb-NO')}</span>
                          </div>
                          <span>{report.area}</span>
                          <span className="font-mono text-xs">{report.sample_count} prover</span>
                        </div>
                        
                        <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                          {report.summary}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => downloadReport(report.id)}
                          className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] transition-all">
                          Apne
                          <ArrowRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
